/**
 * Утиліта кешування даних запитів підтримки в sessionStorage.
 * Кеш живе протягом сесії браузера.
 *
 * Ключ кешу запиту: `support_request_{id}`
 * Ключ кешу списку: `support_letters_list`
 * Ключ кешу історії: `support_request_history_{email}` — тільки дефолтний перегляд
 * (сторінка 1, без фільтрів), формат `{data, total, page, limit}`
 */

const KEY = (id) => `support_request_${id}`;

/** Повертає кешований об'єкт запиту або null */
export const getCachedRequest = (id) => {
	try { return JSON.parse(sessionStorage.getItem(KEY(id))); } catch { return null; }
};

/** Записує об'єкт запиту в кеш */
export const setCachedRequest = (id, data) => {
	try { sessionStorage.setItem(KEY(id), JSON.stringify(data)); } catch {}
};

/** Видаляє кеш конкретного запиту (після мутацій) */
export const invalidateCachedRequest = (id) => sessionStorage.removeItem(KEY(id));

/** Видаляє кеш списку листів (після зміни статусу/сесії/нового запиту) */
export const invalidateLettersList = () => sessionStorage.removeItem('support_letters_list');

const SESSION_KEY = (id) => `support_session_${id}`;

/** Повертає кешовану сесію або null */
export const getCachedSession = (id) => {
	try { return JSON.parse(sessionStorage.getItem(SESSION_KEY(id))); } catch { return null; }
};

/** Записує сесію в кеш */
export const setCachedSession = (id, data) => {
	try { sessionStorage.setItem(SESSION_KEY(id), JSON.stringify(data)); } catch {}
};

/** Видаляє кеш сесії */
export const invalidateCachedSession = (id) => sessionStorage.removeItem(SESSION_KEY(id));

/** Точкове оновлення кешованої сесії через функцію-патч. Якщо кешу нема — нічого не робить. */
export const patchCachedSession = (id, patchFn) => {
	const cached = getCachedSession(id);
	if (!cached) return;
	setCachedSession(id, patchFn(cached));
};

const HISTORY_KEY = (email) => `support_request_history_${email}`;

/** Повертає кешовану історію (paginated-об'єкт {data, total, page, limit}) або null */
export const getCachedSupportHistory = (email) => {
	try { return JSON.parse(sessionStorage.getItem(HISTORY_KEY(email))); } catch { return null; }
};

/** Записує історію в кеш. Писати тільки дефолтний перегляд (page 1, без фільтрів). */
export const setCachedSupportHistory = (email, data) => {
	try { sessionStorage.setItem(HISTORY_KEY(email), JSON.stringify(data)); } catch {}
};

/** Видаляє кеш історії */
export const invalidateCachedSupportHistory = (email) => sessionStorage.removeItem(HISTORY_KEY(email));

/** Точкове оновлення кешованої історії через функцію-патч (paginated-об'єкт). */
export const patchCachedSupportHistory = (email, patchFn) => {
	const cached = getCachedSupportHistory(email);
	if (!cached) return;
	setCachedSupportHistory(email, patchFn(cached));
};
